import { useSyncExternalStore, type MouseEventHandler, type ReactNode } from 'react';
import type { FieldDraftBinding, FieldDraftStore } from './field-drafts';
import './field-review-issue.css';
import {
  FieldOrderHandle,
  fieldOrderDrop,
  fieldOrderOver,
  type FieldReorder,
} from './field-display-order';

/**
 * Shared row presentation only. The caller owns the editor, evidence and save.
 *
 * No confirm circle (single confirm, October 5, 2026): a value is confirmed with its whole
 * document, by the one main button. Typing a value is still its own answer.
 *
 * A value that needs a look says why under its box, always shown (owner, 2026-10-14). It sat
 * behind "Why review? +" or "Check value +", so the operator opened each field to learn what
 * the screen already knew (review pane concerns, 2026-10-13).
 */
export function FieldReviewRow({
  store,
  binding,
  value,
  verified,
  required,
  className = '',
  choicesDisabled = false,
  labelActions,
  adornment,
  detail,
  choices,
  warning,
  source,
  onMouseEnter,
  onMouseLeave,
  children,
  reorder,
}: {
  store: FieldDraftStore;
  binding: FieldDraftBinding;
  value: string;
  verified?: boolean;
  required?: boolean;
  className?: string;
  choicesDisabled?: boolean;
  labelActions?: ReactNode;
  adornment?: ReactNode;
  detail?: ReactNode;
  /** Why the value needs a look, in a few words: marks the row and is said under its box. */
  warning?: ReactNode;
  /** The rows a lookup tied between, each one click to use. */
  choices?: { rowNumber: number; value: string; onPick: () => void }[];
  source?: ReactNode;
  onMouseEnter?: MouseEventHandler<HTMLLabelElement>;
  onMouseLeave?: MouseEventHandler<HTMLLabelElement>;
  children: ReactNode;
  reorder?: FieldReorder;
}) {
  useSyncExternalStore(store.subscribe, store.getSnapshot);
  const retained = store.get(binding);
  const renamed = Boolean(retained && retained.name !== binding.name);
  const draft = renamed ? undefined : retained;
  // A server-confirmed baseline must not colour the operator's unsaved typing.
  // The existing editor still owns every draft/admission/conflict decision.
  const confirmed = Boolean(verified && !retained);
  const saving = draft?.state === 'saving';
  return (
    <label
      data-field-id={binding.fieldId}
      onDragOver={reorder ? fieldOrderOver : undefined}
      onDragLeave={(event) =>
        event.currentTarget.classList.remove('field-order-target', 'field-order-after')
      }
      onDrop={reorder ? (event) => fieldOrderDrop(event, reorder) : undefined}
      className={`field-review-row ${className}${confirmed ? ' field-verified-row' : ''}${warning && !confirmed ? ' needs-review' : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span className="field-review-label">
        {reorder && <FieldOrderHandle order={reorder} />}
        <span>
          {binding.name}
          {required && <sup>*</sup>}
        </span>
        {labelActions}
        {draft && (
          <small className="field-review-save-state">{saving ? 'Saving…' : 'Not saved'}</small>
        )}
      </span>
      <div className="field-review-value">
        {children}
        {adornment}
      </div>
      {warning && !confirmed && <small className="field-review-reason">{warning}</small>}
      {detail && <small className="field-review-detail">{detail}</small>}
      {choices && choices.length > 0 && !draft && (
        <div
          className="field-review-choices"
          role="group"
          aria-label={`Rows that fit ${binding.name}`}
        >
          <span className="field-review-choices-label">Pick one:</span>
          {choices.map((choice) => (
            <button
              key={choice.rowNumber}
              type="button"
              className="field-review-choice"
              disabled={choicesDisabled || saving}
              aria-label={`Use ${choice.value || 'a blank value'} from row ${choice.rowNumber}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.preventDefault();
                choice.onPick();
              }}
            >
              <strong>{choice.value || 'blank'}</strong>
              <small>row {choice.rowNumber}</small>
            </button>
          ))}
        </div>
      )}
      {source && <span className="field-review-source">{source}</span>}
    </label>
  );
}
